import { useState, useEffect } from 'react'
import { UiState, getOnCharacterSelect, getCharacterSelectBtnClick, getOnClientSwitch }  from '../managers/UIManager'
import { getEnabledClients } from '../config/clients'
import { CharacterConfig } from '../types/index'
import { useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'

interface Props {
  state: UiState
  setTab: (tab: UiState['activeTab']) => void
  mode?: 'default' | 'collection'
}

const ROUTE_BY_CLIENT: Record<string, string> = {
  aana: '/AANA',
  pioneers: '/Pioneers',
  'freedom-fighters': '/FreedomFighters',
  rockardemo: '/RockARDemo',
}

export default function ScanScreen({ state, mode = 'default'  }: Props) {
  const [showExperienceSelector, setShowExperienceSelector] = useState(false)
  const navigate = useNavigate()

  const handleRecordToggle = () => {
    document.dispatchEvent(
      new CustomEvent('toggleRecording', { detail: { isRecording: !state.isRecording } })
    )
  }


  // Open experience/character selector automatically
  useEffect(() => {
  if (state.showCharacterSelectBtn) {
    const clickHandler = getCharacterSelectBtnClick()
    if (clickHandler) clickHandler()
  }
}, [state.showCharacterSelectBtn])

  const clients = getEnabledClients().filter(c =>
    ['aana', 'freedom-fighters', 'pioneers', 'roq', 'rockardemo'].includes(c.clientId)
  )

  const handleClientSelect = (clientId: string) => {
    setShowExperienceSelector(false)
    getOnClientSwitch()?.(clientId)

    const route = ROUTE_BY_CLIENT[clientId]
    if (route) navigate(route)
  }

  const handleCharacterSelect = (char: CharacterConfig) => {
    setSelectedCharacterName(char.name)
    setShowExperienceSelector(false)
    getOnCharacterSelect()?.(char)
  }
  // to show which character is selected
  const [selectedCharacterName, setSelectedCharacterName] = useState<string | null>(null)

  const handleReplay = () => {
    document.dispatchEvent(new CustomEvent('replayExperience', { detail: { timestamp: Date.now() } }))
  }

  
  return (
    <div className="scan-screen">
      {/* ROCK AR logo */}
      <div className="scan-logo-wrap">
        <img src="/rockarlogo.png" alt="ROCK AR" className="scan-logo" />
      </div>

      {/* Record button hidden for now in collection/character screens */}

      {/* Scanning frame (shown while searching, hidden once found or during other overlays) */}
      {state.showGuidance &&
      !state.showReplay &&
      !state.showVideoPlayer && (
        <div className="scan-frame">
          <div className="scan-corner scan-tl" />
          <div className="scan-corner scan-tr" />
          <div className="scan-corner scan-bl" />
          <div className="scan-corner scan-br" />
        </div>
      )}

      {/* Guidance text */}
      {state.showGuidance && (
        <div className="scan-guidance">
          <div className="scan-guidance-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="11" x2="12" y2="16" />
              <circle cx="12" cy="7.5" r="0.8" fill="currentColor" stroke="none" />
            </svg>
          </div>

          <p className="scan-guidance-text">{state.guidanceMessage}</p>
        </div>
      )}

      
      {/* Camera feed warning */}
      {state.showCameraWarning && (
        <div className="scan-camera-warning" role="status" aria-live="polite">
          <p>{state.cameraWarningMessage}</p>
        </div>
      )}

      {/* Replay button */}
      {state.showReplay && (
        <button className="scan-replay-btn" onClick={handleReplay}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="5 3 19 12 5 21 5 3" />
          </svg>
          Replay
        </button>
      )}

      {/* Experience selector FAB */}
      {!state.showCharacterSelectBtn && mode === 'default' && (
        <button
          className="scan-selector-fab"
          onClick={() => setShowExperienceSelector(true)}
          aria-label="Choose experience"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
          </svg>
          Choose Experience
        </button>
      )}

      {/* Character carousel — always visible when characters are available */}
      {state.selectorCharacters.length > 0 && createPortal(
        <div className="char-carousel-wrap">
          <div className="char-carousel-viewport">
            <div className="char-carousel-scroll">
              {state.selectorCharacters.map((char) => (
                <button
                  key={char.name}
                  className={`char-carousel-card${char.name === selectedCharacterName ? ' is-selected' : ''}`}
                  onClick={() => handleCharacterSelect(char)}
                >
                  <div className="char-carousel-avatar">
                    {char.thumbnail ? (
                      <img src={char.thumbnail} alt={char.name} />
                    ) : (
                      <div className="scan-sheet-avatar-placeholder" />
                    )}
                  </div>
                  <span className="char-carousel-name">{char.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Client selector bottom sheet (AANA / Pioneers / Freedom Fighters) */}
      {showExperienceSelector && mode === 'default' && (
        <>
          <div className="scan-sheet-backdrop" onClick={() => setShowExperienceSelector(false)} />
          <div className="scan-sheet">
            <div className="scan-sheet-handle" />
            <div className="scan-sheet-header">
              <h3>Choose Your Experience</h3>
              <button className="scan-sheet-close" onClick={() => setShowExperienceSelector(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="scan-sheet-grid">
              {clients.map((client) => (
                <button
                  key={client.clientId}
                  className="scan-sheet-card"
                  onClick={() => handleClientSelect(client.clientId)}
                >
                  <div className="scan-sheet-card-img">
                    <img
                      src={(client as any).experienceItem?.thumbnail || '/rockarlogo.png'}
                      alt={client.clientName}
                    />
                  </div>
                  <span className="scan-sheet-card-name">{client.clientName}</span>
                  <span className="scan-sheet-card-desc">
                    {(client as any).experienceConfig?.aboutText?.split('.')[0]?.slice(0, 60) || 'Description text'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Video player overlay */}
      {state.showVideoPlayer && state.videoUrl && (
        <div className="scan-video-overlay">
          <div className="scan-video-header">
            <h3>Your Recording</h3>
            <button onClick={() => document.dispatchEvent(new CustomEvent('videoRetake'))}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <video src={state.videoUrl} controls autoPlay playsInline className="scan-video" />
        </div>
      )}
    </div>
  )
}
